import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { InformativaPrivacyPDF } from '../components/InformativaPrivacyPDF';

export type PrivacySource = 'AUTO' | 'UPLOAD';

/**
 * Blob dell'Informativa Privacy secondo le Impostazioni:
 * - 'AUTO' → InformativaPrivacyPDF generata con i dati dell'agenzia (ancore già incluse)
 * - altrimenti → PDF caricato manualmente (privacyPolicyUrl), senza ancore
 * null se non c'è alcuna informativa (UPLOAD senza url).
 */
export const resolvePrivacyBlob = async (settingsObj: any): Promise<{ blob: Blob; source: PrivacySource } | null> => {
    if (!settingsObj) return null;
    if (settingsObj.privacyPolicyMode === 'AUTO') {
        const blob = await pdf(<InformativaPrivacyPDF settings={settingsObj} />).toBlob();
        return { blob, source: 'AUTO' };
    }
    const url = settingsObj.privacyPolicyUrl;
    if (!url) return null;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Fetch privacy policy fallito: ${resp.status} ${resp.statusText}`);
    const blob = await resp.blob();
    return { blob, source: 'UPLOAD' };
};

/**
 * Accoda il PDF della privacy a quello principale. Per l'informativa caricata
 * (UPLOAD) aggiunge in basso a destra di ogni pagina un'ancora `#otp_pp{n}#`
 * quasi invisibile per la firma OTP; in AUTO le ancore ci sono già.
 */
export const appendPrivacyPolicy = async (
    mainBlob: Blob,
    privacyBlob: Blob,
    source: PrivacySource = 'UPLOAD'
): Promise<Blob> => {
    const [mainBytes, privBytes] = await Promise.all([
        mainBlob.arrayBuffer(),
        privacyBlob.arrayBuffer(),
    ]);
    const merged = await PDFDocument.create();
    const mainDoc = await PDFDocument.load(mainBytes, { ignoreEncryption: true });
    const privDoc = await PDFDocument.load(privBytes, { ignoreEncryption: true });
    const mainPagesCopied = await merged.copyPages(mainDoc, mainDoc.getPageIndices());
    mainPagesCopied.forEach(p => merged.addPage(p));
    const privPagesCopied = await merged.copyPages(privDoc, privDoc.getPageIndices());

    // Sulla privacy caricata a mano serve un'ancora testuale in basso a destra di
    // ogni pagina, così il servizio di firma sa dove posizionare il campo.
    let helvetica: any = null;
    if (source === 'UPLOAD') {
        try { helvetica = await merged.embedFont(StandardFonts.Helvetica); }
        catch (e) { console.warn('[appendPrivacyPolicy] embed font fallito, salto ancore:', e); }
    }

    privPagesCopied.forEach((page, idx) => {
        merged.addPage(page);
        if (source === 'UPLOAD' && helvetica) {
            const { width } = page.getSize();
            const anchor = `#otp_pp${idx + 1}#`;
            const fontSize = 8;
            const textWidth = helvetica.widthOfTextAtSize(anchor, fontSize);
            page.drawText(anchor, {
                x: width - textWidth - 25, // Margine destro 25pt
                y: 18,                      // 18pt dal bordo inferiore
                size: fontSize,
                font: helvetica,
                color: rgb(0.98, 0.98, 0.98), // Circa #fafafa, quasi invisibile su bianco
            });
        }
    });

    // Numeri di pagina continui sul documento unito. react-pdf numera ogni <Document>
    // separatamente (il mandato "di 5", l'informativa riparte da 1), e i numeri sono già
    // nei blob. Copriamo il numero centrato in basso e lo ristampiamo come "Pag. i di N"
    // sul totale. Le ancore OTP sono in basso a destra, quindi non vengono coperte.
    try {
        const numFont = helvetica || await merged.embedFont(StandardFonts.Helvetica);
        const allPages = merged.getPages();
        const totalPages = allPages.length;
        const grey = rgb(0.533, 0.533, 0.533); // #888888, come il footer react-pdf
        allPages.forEach((page, i) => {
            const { width } = page.getSize();
            // Copre il numero originale (fascia centrale in basso).
            page.drawRectangle({ x: width * 0.30, y: 3, width: width * 0.40, height: 17, color: rgb(1, 1, 1) });
            // Ristampa il numero progressivo corretto.
            const label = `Pag. ${i + 1} di ${totalPages}`;
            const size = 8;
            const tw = numFont.widthOfTextAtSize(label, size);
            page.drawText(label, { x: (width - tw) / 2, y: 8, size, font: numFont, color: grey });
        });
    } catch (e) {
        console.warn('[appendPrivacyPolicy] ristampa numeri pagina continui fallita:', e);
    }

    const mergedBytes = await merged.save();
    return new Blob([mergedBytes], { type: 'application/pdf' });
};

/**
 * Immagine in PNG base64 per react-pdf, che non supporta .webp e non gestisce bene le query
 * string delle URL presigned S3. null se manca o non si riesce a convertire.
 */
export const immagineInPng = async (url: string | null | undefined): Promise<string | null> => {
    if (!url) return null;
    try {
        const res = await fetch(url, { mode: 'cors' });
        const blob = await res.blob();
        const bmp = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = bmp.width;
        canvas.height = bmp.height;
        canvas.getContext('2d')!.drawImage(bmp, 0, 0);
        return canvas.toDataURL('image/png');
    } catch (e) {
        console.warn('PDF image conversion failed:', e);
        return null;
    }
};
